import React, { useState, useEffect, useContext, useCallback } from 'react'
import update from 'immutability-helper'

import View from './View'
import {
  shapeClassname,
  toolboxActionHandler,
  positionCommentPlace,
  typeDisplayAnnotation,
} from '../../shared/constants'
import {
  convertToCanvasGroupPosition,
  getGroupContainer,
  createShapeObject,
  convertToScreenPosition,
  isDrawingArea,
} from '../../shared/utils'
import { PreviewContext } from '../../context/previewContext'
import { TooltipContext } from '../../context/tooltipContext'
import { ContextMenuContext } from '../../context/contextMenuContext'
import { ShapeContext } from '../../context/shapeContext'
import { PdfContext } from '../../context/pdfContext'

const CanvasView = () => {
  //#region state
  const [startPos, setStartPos] = useState(null)
  const [points, setPoints] = useState([])
  const [isPaint, setIsPaint] = useState(false)
  const [isObjectHasCreated, setIsObjectHasCreated] = useState(false)
  const [index, setIndex] = useState(0)
  const [startX, setStartX] = useState(0)
  const [scrollLeft, setScrollLeft] = useState(0)
  const [startY, setStartY] = useState(0)
  const [scrollTop, setScrollTop] = useState(0)
  const [drawObject, setDrawObject] = useState(null)
  const [objects, setObjects] = useState([])
  const [groupTarget, setGroupTarget] = useState(-1)

  const shapeContext = useContext(ShapeContext)
  const previewContext = useContext(PreviewContext)
  const pdfContext = useContext(PdfContext)
  const tooltipContext = useContext(TooltipContext)
  const contextMenuContext = useContext(ContextMenuContext)

  const canvasObjects = shapeContext.canvasObjects
  const updateCanvasObjects = shapeContext.updateCanvasObjects
  const groupCount = shapeContext.groupCount
  const onHasUnsavedComment = tooltipContext.onHasUnsavedComment
  const isShowTooltip = tooltipContext.isShowTooltip
  const onCloseTooltip = tooltipContext.onCloseTooltip
  const { activeDrawingArea } = previewContext
  //#endregion

  //#region useEffect

  // split canvas object into each group position
  useEffect(() => {
    if (canvasObjects.length > 0) {
      const results = []

      for (let index = 0; index < groupCount; index++) {
        const shapes = canvasObjects.filter((x) => x.attrs.group === index)

        if (!results[index]) {
          results[index] = []
        }

        if (shapes.length > 0) {
          results[index] = [...shapes]
        }
      }

      setObjects(results)
    } else {
      setObjects([])
    }
  }, [canvasObjects, groupCount])
  //#endregion

  //#region function
  const createObject = useCallback(
    (e) => {
      if (groupTarget === -1) {
        // do nothing
        return
      }

      const groupAttrs = e.currentTarget.children[0].children[groupTarget].attrs
      const startPosGroupArea = convertToCanvasGroupPosition(
        startPos,
        groupAttrs
      )
      const movePos = e.currentTarget.getPointerPosition()
      const movePosGroupArea = convertToCanvasGroupPosition(movePos, groupAttrs)

      let pencilPoints = []

      // pencil only
      if (previewContext.toolboxComponentAction.type === shapeClassname.LINE) {
        const drawArea = {
          x: groupAttrs.x,
          y: groupAttrs.y,
          maxWidth: groupAttrs.x + groupAttrs.width,
          maxHeight: groupAttrs.y + groupAttrs.height,
        }
        let pencilPoint = {
          x: movePosGroupArea.x,
          y: movePosGroupArea.y,
        }

        // check area allowed drawing
        // x area
        if (movePos.x > drawArea.maxWidth) {
          pencilPoint.x = groupAttrs.width
        } else if (movePos.x < drawArea.x) {
          pencilPoint.x = 0
        }

        // check area allowed drawing
        // y area
        if (movePos.y > drawArea.maxHeight) {
          pencilPoint.y = groupAttrs.height
        } else if (movePos.y < drawArea.y) {
          pencilPoint.y = 0
        }

        pencilPoints = points.concat([pencilPoint.x, pencilPoint.y])
        setPoints(pencilPoints)
      }

      const obj = createShapeObject(
        previewContext.toolboxComponentAction,
        groupAttrs,
        startPosGroupArea,
        movePosGroupArea,
        pencilPoints,
        previewContext.toolboxComponentAction.color,
        {
          group: groupTarget,
          groupAttrs: groupAttrs,
          version: 0.2,
        }
      )

      if (obj !== null) {
        setIsObjectHasCreated(true)
        updateCanvasObjects((x) =>
          update(x, {
            $splice: [[index, 1, obj]],
          })
        )

        return obj
      }
    },
    [
      groupTarget,
      index,
      points,
      previewContext.toolboxComponentAction,
      startPos,
      updateCanvasObjects,
    ]
  )

  const createDotObject = useCallback(
    (e) => {
      if (groupTarget === -1) {
        // do nothing
        return
      }

      const groupAttrs = e.currentTarget.children[0].children[groupTarget].attrs
      const pos = convertToCanvasGroupPosition(
        e.currentTarget.getPointerPosition(),
        groupAttrs
      )

      const obj = createShapeObject(
        previewContext.toolboxComponentAction,
        groupAttrs,
        pos,
        null,
        [],
        previewContext.toolboxComponentAction.color,
        {
          group: groupTarget,
          groupAttrs: groupAttrs,
          version: 0.2,
        }
      )

      // removeUnsavedObject();

      setIsObjectHasCreated(true)
      updateCanvasObjects((x) =>
        update(x, {
          $push: [obj],
        })
      )

      return obj
    },
    [groupTarget, previewContext.toolboxComponentAction, updateCanvasObjects]
  )

  const getGroupAttrs = useCallback((e, name) => {
    const groups = e.currentTarget.children[0].children
    const idx = groups.findIndex((x) => x.attrs.name === name)
    if (idx > -1) {
      return groups[idx].attrs
    }

    return null
  }, [])

  const updateTooltipPlacement = useCallback(
    (pos) => {
      const height = window.innerHeight / 2

      if (pos.y <= height) {
        tooltipContext.onTooltipPlaceChanged(positionCommentPlace.BOTTOM)
      } else {
        tooltipContext.onTooltipPlaceChanged(positionCommentPlace.TOP)
      }
    },
    [tooltipContext]
  )

  /**
   * Blocks placing a new shape only while one is still being written. A shape
   * that already has a comment is staged for the next Save Changes and must not
   * block anything - batching depends on several of them coexisting.
   */
  const isHasUnsavedTopic = useCallback(() => {
    const idx = canvasObjects.findIndex((x) => x.isUnsaved && !x.unsavedComment)
    return idx > -1
  }, [canvasObjects])

  // show comment when click object
  const showCommentOnClickShape = useCallback(
    (e, targetShape) => {
      const className = targetShape.getClassName()
      let attrs = targetShape.attrs

      if (className === shapeClassname.LINE) {
        attrs = {
          x: targetShape.points()[0],
          y: targetShape.points()[1],
        }
      }

      let currentObject = targetShape
      const idx = canvasObjects.findIndex(
        (x) => x.attrs.id === targetShape.attrs.id
      )
      if (idx > -1) {
        currentObject = canvasObjects[idx]
      }

      const groupAttrs = getGroupAttrs(e, targetShape.attrs.groupAttrs.name)
      if (groupAttrs) {
        const shapePos = convertToScreenPosition(attrs, groupAttrs)

        // show comment by selected obj
        tooltipContext.onSelectedCommentTooltip(
          targetShape.attrs.id,
          shapePos,
          currentObject
        )

        updateTooltipPlacement(shapePos)
      }
    },
    [canvasObjects, getGroupAttrs, tooltipContext, updateTooltipPlacement]
  )

  const handleStageMouseDown = useCallback(
    (e) => {
      // hide context menu (right click)
      contextMenuContext.onIsShowcontextMenu(false)

      // only left mouse button
      if (e.evt.button !== 0) return

      const targetShape = e.currentTarget.targetShape
      const isMarkerClick =
        !!targetShape && !isDrawingArea(targetShape.attrs.name)

      /**
       * An open comment editor consumes the next canvas click: that click only
       * dismisses the editor, so placing the following marker is a deliberate
       * second click rather than a side effect of dismissing. Clicking straight
       * onto another marker is the exception - it switches thread in one click,
       * and has to skip the guards below because the dismissed editor's own
       * shape is still in `canvasObjects` until the state update commits.
       */
      if (isShowTooltip) {
        onCloseTooltip()

        if (!isMarkerClick) return
      } else {
        /**
         * check area allowed drawing
         * return if click outside area drawing
         */
        if (!targetShape) return

        if (isHasUnsavedTopic()) return
      }

      tooltipContext.onIsCloseTooltipEvent(false)

      const mousePos = e.currentTarget.getPointerPosition()
      const groups = e.currentTarget.children[0].children
      const currentGroupTarget = getGroupContainer(mousePos, groups)

      // drag pdf scrol to
      if (
        previewContext.typeDisplay === typeDisplayAnnotation.PDF &&
        previewContext.toolboxComponentAction.handler ===
          toolboxActionHandler.MOUSE_CLICK
      ) {
        setStartX(e.evt.pageX - pdfContext.pdfViewerWrappRef.current.offsetLeft)
        setScrollLeft(pdfContext.pdfViewerWrappRef.current.scrollLeft)

        setStartY(e.evt.pageY - pdfContext.pdfViewerWrappRef.current.offsetTop)
        setScrollTop(pdfContext.pdfViewerWrappRef.current.scrollTop)
      }

      // all ready get group position
      const groupAttrs = targetShape.parent.attrs

      if (previewContext.toolboxComponentAction !== null) {
        if (targetShape && isDrawingArea(targetShape.attrs.name)) {
          // draging shape event
          const pos = mousePos
          setStartPos(pos)
          setIndex(canvasObjects.length)

          // pencil only
          if (
            previewContext.toolboxComponentAction.type === shapeClassname.LINE
          ) {
            const updatedPosByGroup = convertToCanvasGroupPosition(
              pos,
              groupAttrs
            )
            setPoints([updatedPosByGroup.x, updatedPosByGroup.y])
          }

          previewContext.onImageBoundRectChanged((v) =>
            update(v, {
              relativeX: { $set: e.evt.pageX - v.x },
              relativeY: { $set: e.evt.pageY - v.y },
            })
          )

          setIsPaint(true)
        }
      }

      setGroupTarget(currentGroupTarget)

      e.evt.stopPropagation()
      e.evt.preventDefault()
    },
    [
      contextMenuContext,
      isHasUnsavedTopic,
      isShowTooltip,
      onCloseTooltip,
      tooltipContext,
      previewContext,
      pdfContext.pdfViewerWrappRef,
      canvasObjects.length,
    ]
  )

  const handleStageTouchStart = useCallback(
    (e) => {
      // hide context menu (right click)
      contextMenuContext.onIsShowcontextMenu(false)

      const targetShape = e.currentTarget.tapStartShape
      const isMarkerClick =
        !!targetShape && !isDrawingArea(targetShape.attrs.name)

      // see handleStageMouseDown - an open editor consumes the next tap
      if (isShowTooltip) {
        onCloseTooltip()

        if (!isMarkerClick) return
      } else {
        /**
         * check area allowed drawing
         * return if click outside area drawing
         */
        if (!targetShape) return

        if (isHasUnsavedTopic()) return
      }

      tooltipContext.onIsCloseTooltipEvent(false)

      const mousePos = e.currentTarget.getPointerPosition()

      const groups = e.currentTarget.children[0].children

      const currentGroupTarget = getGroupContainer(mousePos, groups)

      // drag pdf scrol to
      if (previewContext.typeDisplay === typeDisplayAnnotation.PDF) {
        setStartX(
          e.evt.touches[0].pageX -
            pdfContext.pdfViewerWrappRef.current.offsetLeft
        )
        setScrollLeft(pdfContext.pdfViewerWrappRef.current.scrollLeft)

        setStartY(
          e.evt.touches[0].pageY -
            pdfContext.pdfViewerWrappRef.current.offsetTop
        )
        setScrollTop(pdfContext.pdfViewerWrappRef.current.scrollTop)
      }

      // all ready get group position
      const groupAttrs = targetShape.parent.attrs

      if (previewContext.toolboxComponentAction !== null) {
        if (targetShape && isDrawingArea(targetShape.attrs.name)) {
          // draging shape event
          const pos = mousePos
          setStartPos(pos)
          setIndex(canvasObjects.length)

          // pencil only
          if (
            previewContext.toolboxComponentAction.type === shapeClassname.LINE
          ) {
            const updatedPosByGroup = convertToCanvasGroupPosition(
              pos,
              groupAttrs
            )

            setPoints([updatedPosByGroup.x, updatedPosByGroup.y])
          }

          previewContext.onImageBoundRectChanged((v) =>
            update(v, {
              relativeX: { $set: e.evt.touches[0].pageX - v.x },
              relativeY: { $set: e.evt.touches[0].pageY - v.y },
            })
          )

          setIsPaint(true)
        }
      }

      setGroupTarget(currentGroupTarget)

      e.evt.stopPropagation()
      e.evt.preventDefault()
    },
    [
      contextMenuContext,
      isHasUnsavedTopic,
      isShowTooltip,
      onCloseTooltip,
      tooltipContext,
      previewContext,
      pdfContext.pdfViewerWrappRef,
      canvasObjects.length,
    ]
  )

  const handleStageMouseMove = useCallback(
    (e) => {
      // only left mouse button
      if (e.evt.button !== 0) return

      if (previewContext.toolboxComponentAction !== null && isPaint) {
        const movePos = e.currentTarget.getPointerPosition()

        if (
          previewContext.toolboxComponentAction.handler ===
          toolboxActionHandler.MOUSE_CLICK
        ) {
          // hide comment when dragging
          tooltipContext.onShowTooltip(false)

          if (previewContext.typeDisplay === typeDisplayAnnotation.IMAGE) {
            // detect has moving
            if (startPos.x !== movePos.x || startPos.y !== movePos.y) {
              // drag image handler
              previewContext.onImageBoundRectChanged((v) =>
                update(v, {
                  x: { $set: e.evt.pageX - v.relativeX },
                  y: { $set: e.evt.pageY - v.relativeY },
                })
              )
            }
          } else {
            // drag pdf and move container scroll
            const x =
              e.evt.pageX - pdfContext.pdfViewerWrappRef.current.offsetLeft
            const walkX = (x - startX) * 1 //scroll-fast
            const y =
              e.evt.pageY - pdfContext.pdfViewerWrappRef.current.offsetTop
            const walkY = (y - startY) * 1 //scroll-fast

            pdfContext.pdfViewerWrappRef.current.scrollLeft = scrollLeft - walkX
            pdfContext.pdfViewerWrappRef.current.scrollTop = scrollTop - walkY
          }

          if (!previewContext.isDragging) {
            previewContext.onIsDragging(true)
            previewContext.onTransitionProperty('none')
          }
        } else {
          setDrawObject(createObject(e))
        }
      }

      e.evt.stopPropagation()
      e.evt.preventDefault()
    },
    [
      createObject,
      isPaint,
      pdfContext.pdfViewerWrappRef,
      previewContext,
      scrollLeft,
      scrollTop,
      startPos,
      startX,
      startY,
      tooltipContext,
    ]
  )

  const handleStageTouchMove = useCallback(
    (e) => {
      if (previewContext.toolboxComponentAction !== null && isPaint) {
        const movePos = e.currentTarget.getPointerPosition()

        if (
          previewContext.toolboxComponentAction.handler ===
          toolboxActionHandler.MOUSE_CLICK
        ) {
          // hide comment when dragging
          tooltipContext.onShowTooltip(false)

          if (previewContext.typeDisplay === typeDisplayAnnotation.IMAGE) {
            if (startPos.x !== movePos.x || startPos.y !== movePos.y) {
              // drag image handler
              previewContext.onImageBoundRectChanged((v) => {
                return update(v, {
                  x: { $set: e.evt.touches[0].pageX - v.relativeX },
                  y: { $set: e.evt.touches[0].pageY - v.relativeY },
                })
              })
            }
          } else {
            // drag pdf and move container scroll
            const x =
              e.evt.touches[0].pageX -
              pdfContext.pdfViewerWrappRef.current.offsetLeft
            const walkX = (x - startX) * 1 //scroll-fast
            const y =
              e.evt.touches[0].pageY -
              pdfContext.pdfViewerWrappRef.current.offsetTop
            const walkY = (y - startY) * 1 //scroll-fast

            pdfContext.pdfViewerWrappRef.current.scrollLeft = scrollLeft - walkX
            pdfContext.pdfViewerWrappRef.current.scrollTop = scrollTop - walkY
          }

          if (!previewContext.isDragging) {
            previewContext.onIsDragging(true)
            previewContext.onTransitionProperty('none')
          }
        } else {
          setDrawObject(createObject(e))
        }
      }
      e.evt.stopPropagation()
      e.evt.preventDefault()
    },
    [
      createObject,
      isPaint,
      pdfContext.pdfViewerWrappRef,
      previewContext,
      scrollLeft,
      scrollTop,
      startPos,
      startX,
      startY,
      tooltipContext,
    ]
  )

  const handleStageMouseUp = useCallback(
    (e) => {
      // only left mouse button
      if (e.evt.button !== 0) return

      const targetShape = e.currentTarget.targetShape
      const currentPos = e.currentTarget.getPointerPosition()

      // only for dot object
      if (
        isPaint &&
        previewContext.toolboxComponentAction !== null &&
        previewContext.toolboxComponentAction.handler ===
          toolboxActionHandler.MOUSE_CLICK &&
        previewContext.toolboxComponentAction.type === shapeClassname.CIRCLE &&
        !previewContext.isDragging &&
        (!targetShape || isDrawingArea(targetShape.attrs.name))
      ) {
        const objDotCreated = createDotObject(e)
        updateTooltipPlacement(currentPos)

        tooltipContext.onSelectedCommentTooltip(
          objDotCreated.attrs.id,
          startPos,
          objDotCreated
        )

        // we always know, every new object created, is unsaved
        onHasUnsavedComment(true)
      }

      /**
       * show comment after shape created
       * after painting object done
       */
      if (isPaint && !previewContext.isDragging && isObjectHasCreated) {
        updateTooltipPlacement(currentPos)

        tooltipContext.onSelectedCommentTooltip(
          drawObject.attrs.id,
          startPos,
          drawObject
        )

        // we always know, every new object created, is unsaved
        onHasUnsavedComment(true)
      } else if (
        !previewContext.isDragging &&
        targetShape &&
        !isDrawingArea(targetShape.attrs.name)
      ) {
        // show comment when click object. all object
        showCommentOnClickShape(e, targetShape)
      }

      setIsPaint(false)
      previewContext.onIsDragging(false)
      previewContext.onTransitionProperty('all')
      setIsObjectHasCreated(false)

      e.evt.stopPropagation()
      e.evt.preventDefault()
    },
    [
      createDotObject,
      drawObject,
      isObjectHasCreated,
      isPaint,
      onHasUnsavedComment,
      previewContext,
      showCommentOnClickShape,
      startPos,
      tooltipContext,
      updateTooltipPlacement,
    ]
  )

  const handleStageTouchEnd = useCallback(
    (e) => {
      const targetShape = e.currentTarget.tapStartShape
      const currentPos = e.currentTarget.getPointerPosition()

      // only for dot object
      if (
        isPaint &&
        previewContext.toolboxComponentAction !== null &&
        previewContext.toolboxComponentAction.type === shapeClassname.CIRCLE &&
        !previewContext.isDragging &&
        (!targetShape || isDrawingArea(targetShape.attrs.name))
      ) {
        const objDotCreated = createDotObject(e)
        if (!objDotCreated) return
        updateTooltipPlacement(currentPos)
        tooltipContext.onSelectedCommentTooltip(
          objDotCreated.attrs.id,
          startPos,
          objDotCreated
        )

        // we always know, every new object created, is unsaved
        onHasUnsavedComment(true)
      }

      /**
       * show comment after shape created
       * after painting object done
       */
      if (isPaint && !previewContext.isDragging && isObjectHasCreated) {
        updateTooltipPlacement(currentPos)

        tooltipContext.onSelectedCommentTooltip(
          drawObject.attrs.id,
          startPos,
          drawObject
        )

        // we always know, every new object created, is unsaved
        onHasUnsavedComment(true)
      } else if (
        !previewContext.isDragging &&
        targetShape &&
        !isDrawingArea(targetShape.attrs.name)
      ) {
        // show comment when click object. all object
        showCommentOnClickShape(e, targetShape)
      }

      setIsPaint(false)
      previewContext.onIsDragging(false)
      previewContext.onTransitionProperty('all')
      setIsObjectHasCreated(false)

      e.evt.stopPropagation()
      e.evt.preventDefault()
    },
    [
      createDotObject,
      drawObject,
      isObjectHasCreated,
      isPaint,
      onHasUnsavedComment,
      previewContext,
      showCommentOnClickShape,
      startPos,
      tooltipContext,
      updateTooltipPlacement,
    ]
  )

  const handleContextMenu = useCallback(
    (e) => {
      // show menu
      // set position menu
      // cancel drawing
      contextMenuContext.onPositionContextMenu({
        x: e.evt.pageX,
        y: e.evt.pageY,
      })
      contextMenuContext.onIsShowcontextMenu(true)

      e.evt.stopPropagation()
      e.evt.preventDefault()
    },
    [contextMenuContext]
  )
  //#endregion

  return (
    <View
      groupCount={groupCount}
      onMouseDown={handleStageMouseDown}
      onTouchStart={handleStageTouchStart}
      onMouseUp={handleStageMouseUp}
      onTouchEnd={handleStageTouchEnd}
      onMouseMove={handleStageMouseMove}
      onTouchMove={handleStageTouchMove}
      onContextMenu={handleContextMenu}
      imageBoundRect={previewContext.imageBoundRect}
      layerPdfScroll={pdfContext.layerPdfScroll}
      typeDisplay={previewContext.typeDisplay}
      isMouseDrawAction={
        previewContext.toolboxComponentAction.type !== shapeClassname.MOUSE
      }
      activeDrawingArea={activeDrawingArea}
      objects={objects}
    />
  )
}

export default React.memo(CanvasView)
